import type * as zod from 'zod';

import { hardwareConfigEquipmentMappingToEquipmentKeyToEquipmentId } from '@sb/ai/helpers';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types/CameraConfiguration';
import type { GPUTaskRunner } from '@sb/integrations/implementations/GPUTaskRunner/implementation/GPUTaskRunner';
import type { GPUTaskRunnerState } from '@sb/integrations/implementations/GPUTaskRunner/types';
import type { SchunkEGxConfiguration } from '@sb/integrations/implementations/schunk/SchunkEGx/types/SchunkEGxConfiguration';
import type { StandardBotsRO1 } from '@sb/integrations/implementations/StandardBotsRO1/implementation/StandardBotsRO1';
import * as log from '@sb/log';
import type { DeviceInfo } from '@sb/messenger/auto_generated/messenger/skills_bot_service';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepFailure } from '@sb/routine-runner/types';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { EventEmitter, wait } from '@sb/utilities';

import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

export default class RunSkillStep extends Step<Arguments, Variables> {
  private events = new EventEmitter<{
    stop: void;
    complete: void;
    failure: StepFailure;
  }>();

  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  /**
   * @internal
   */
  private timeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Save any callbacks to cancel on destroy
   * @internal
   */
  private cancelers: Array<() => void> = [];

  public initializeVariableState(): void {
    this.variables = {};
  }

  private getSecondaryBotEquipments(): StandardBotsRO1[] {
    const connectedBots = this.routineContext.equipment.getDevicesByType(
      'StandardBotsRO1',
    ) as StandardBotsRO1[];

    return connectedBots.filter((bot) => bot.update().connected);
  }

  private getGpuTaskRunner(): GPUTaskRunner {
    const equipment = this.routineContext.equipment.getDeviceById(
      this.args.gpuTaskRunnerId,
    );

    if (equipment === undefined) {
      throw new Error(
        `Could not find equipment with id ${this.args.gpuTaskRunnerId}`,
      );
    }

    const gpuTaskRunner = equipment as GPUTaskRunner;

    return gpuTaskRunner;
  }

  private equipmentIdToDeviceData(equipmentId: string): DeviceInfo {
    const sensorEquipment =
      this.routineContext.equipment.getDeviceById(equipmentId);

    if (!sensorEquipment) {
      throw new Error(`Could not find equipment with id ${equipmentId}`);
    }

    let deviceData: DeviceInfo;

    switch (sensorEquipment.kind) {
      case 'Camera': {
        const cameraConfig = sensorEquipment.getConfig() as CameraConfiguration;

        deviceData = {
          equipmentId,
          deviceId: cameraConfig.id,
          type: 'camera',
          cameraConfig: cameraConfig.configuration,
        };

        break;
      }
      case 'SchunkEGx':
        deviceData = {
          equipmentId,
          deviceId: (sensorEquipment.getConfig() as SchunkEGxConfiguration)
            .variant,
          type: 'gripper',
        };

        break;
      // TODO add other sensor types as needed
      default:
        throw new Error(`Unsupported equipment kind: ${sensorEquipment.kind}`);
    }

    return deviceData;
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunSkill',
        },
        failureReason: 'Run skill step not supported in visualizer',
      });
    }

    try {
      if (!this.args.isBimanual || this.args.isPrimary) {
        if (this.args.isBimanual) {
          const bots = this.getSecondaryBotEquipments();

          while (true) {
            try {
              if (bots.length === 0) {
                throw new Error('Not connected');
              }

              const allBotsReady = await Promise.all(
                bots.map(async (bot) => {
                  const variable =
                    await bot.routineEditorVariablesApi.routineEditorVariablesLoad(
                      'inferenceReady',
                    );

                  return JSON.parse(variable.data.value);
                }),
              );

              if (allBotsReady.every((ready) => ready)) {
                break;
              }
            } catch (error) {
              log.info('Secondary robot(s) inference not ready yet', error);
            }

            await wait(100);
          }

          try {
            await Promise.all(
              bots.map((bot) =>
                bot.routineEditorVariablesApi.routineEditorVariablesUpdate(
                  'inferenceRun',
                  {
                    value: true,
                  },
                ),
              ),
            );
          } catch (error) {
            log.info('Failed to start secondary robot(s) inference', error);
          }
        }

        const gpuTaskRunner = this.getGpuTaskRunner();

        const equipmentState = this.routineContext.equipment
          .getEquipmentState()
          .find((device) => device.id === this.args.gpuTaskRunnerId);

        if (equipmentState === undefined) {
          throw new Error(
            `Could not find state for GPU Task runner ${gpuTaskRunner.getConfig().name} `,
          );
        }

        const gpuTaskRunnerState = equipmentState?.state as GPUTaskRunnerState;

        if (!gpuTaskRunnerState.isConnected) {
          throw new Error(
            `GPU Task runner ${gpuTaskRunner.getConfig().name} is not connected`,
          );
        }

        if (gpuTaskRunnerState.isRunningInference) {
          throw new Error(
            `GPU Task runner ${gpuTaskRunner.getConfig().name}  is already running inference`,
          );
        }

        if (gpuTaskRunnerState.isConfiguringInference) {
          throw new Error(
            `GPU Task runner ${gpuTaskRunner.getConfig().name}  is currently configuring inference`,
          );
        }

        const equipmentKeyToEquipmentId = getFeatureFlagSync(
          'useHardwareConfigs',
          false,
        )
          ? hardwareConfigEquipmentMappingToEquipmentKeyToEquipmentId(
              this.args.hardwareConfigEquipmentMapping,
            )
          : this.args.equipmentKeyToEquipmentId;

        const equipmentDeviceInfo = Object.entries(
          equipmentKeyToEquipmentId,
        ).reduce(
          (deviceMap, [key, equipmentId]) => {
            const deviceData = this.equipmentIdToDeviceData(equipmentId);

            return {
              ...deviceMap,
              [key]: deviceData,
            };
          },
          {} as {
            [key: string]: DeviceInfo;
          },
        );

        await gpuTaskRunner.executeCommand({
          kind: 'GPUTaskRunnerCommand',
          subCommand: {
            command: 'configure_inference',
            modelName: this.args.modelPath,
            robotId: this.routineContext.robotId || 'local',
            equipmentKeyToDeviceInfo: equipmentDeviceInfo,
          },
        });

        await gpuTaskRunner.executeCommand({
          kind: 'GPUTaskRunnerCommand',
          subCommand: {
            command: 'start_inference',
            timeout: this.args.timeout,
          },
        });

        const promise = this.events.next('complete');
        await this._resume();
        await promise;
      } else {
        this.routineContext.setVariableState('inferenceRun', {
          stepKind: 'RunSkill',
          name: 'inferenceRun',
          value: false,
        });

        const promise = this.events.next('complete');
        await this._resume();
        await promise;
      }
    } catch (error) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunSkill',
        },
        failureReason: `Failed to run skill: ${error?.message}`,
        error,
      });
    }
  }

  public async _stop() {
    this._pause();

    if (this.args.isBimanual && this.args.isPrimary) {
      try {
        const bots = this.getSecondaryBotEquipments();

        if (bots.length === 0) {
          throw new Error('Secondary robot(s) not connected');
        }

        bots.forEach((bot) =>
          bot.routineEditorVariablesApi.routineEditorVariablesUpdate(
            'inferenceRun',
            {
              value: false,
            },
          ),
        );
      } catch (error) {
        log.info('Failed to stop secondary robot(s) inference', error);
      }
    }

    this.cancelers.forEach((cancel) => cancel());
    this.events.emit('stop');
  }

  public async _pause(): Promise<void> {
    if (this.args.isBimanual && !this.args.isPrimary) {
      this.routineContext.setVariableState('inferenceReady', {
        stepKind: 'RunSkill',
        name: 'inferenceReady',
        value: false,
      });
    }

    if (getFeatureFlagSync('useControlSourceManager', false)) {
      let source = 'ROS_POSE';

      if (getFeatureFlagSync('zmqPoseMigration', false)) {
        source = 'SKILLS_BOT';
      } else if (getFeatureFlagSync('complianceMode', false)) {
        source = 'ROS_EFFORT';
      }

      await this.routineContext.armControlBotConnector?.ReleaseControlFromSource(
        {
          value: source,
        },
      );
    } else {
      await this.routineContext.robot.setROSControlEnabled(false);
    }

    if (this.timeout !== null) {
      clearTimeout(this.timeout);
    }

    try {
      const gpuTaskRunner = this.getGpuTaskRunner();

      await gpuTaskRunner.executeCommand({
        kind: 'GPUTaskRunnerCommand',
        subCommand: {
          command: 'stop_inference',
        },
      });
    } catch (error) {
      log.warn(
        'gpuTaskRunner.stopInference',
        `Failed To stop inference on GPU Task Runner: ${error}`,
      );
    }

    // TODO: add pause once skills bot can support it
    this.events.emit('failure', {
      failure: {
        kind: FailureKind.InferenceFailure,
      },
      failureReason: 'Pause on "run skill" step is not supported.',
      error: new Error('Pause on "run skill" step is not supported.'),
    });
  }

  public async _resume(): Promise<void> {
    if (this.args.isBimanual && !this.args.isPrimary) {
      this.routineContext.setVariableState('inferenceReady', {
        stepKind: 'RunSkill',
        name: 'inferenceReady',
        value: true,
      });

      let stopped = false;

      this.events.once('stop', () => {
        stopped = true;
      });

      const inferenceRunCancel = this.routineContext.onVariablesChange(
        (runVariables) => {
          if (
            runVariables.inferenceRun?.stepKind === 'RunSkill' &&
            runVariables.inferenceRun?.value === true
          ) {
            if (getFeatureFlagSync('useControlSourceManager', false)) {
              let source = 'ROS_POSE';

              if (getFeatureFlagSync('zmqPoseMigration', false)) {
                source = 'SKILLS_BOT';
              } else if (getFeatureFlagSync('complianceMode', false)) {
                source = 'ROS_EFFORT';
              }

              this.routineContext.armControlBotConnector?.SetControlSource({
                value: source,
              });
            } else {
              this.routineContext.robot.setROSControlEnabled(true);
            }

            const inferenceStopCancel = this.routineContext.onVariablesChange(
              (stopVariables) => {
                if (
                  stopVariables.inferenceRun?.stepKind === 'RunSkill' &&
                  stopVariables.inferenceRun?.value === false
                ) {
                  if (stopped) {
                    return;
                  }

                  this.events.emit('complete');
                }
              },
            );

            this.cancelers.push(inferenceStopCancel);
          }
        },
      );

      this.cancelers.push(inferenceRunCancel);
    } else {
      if (getFeatureFlagSync('useControlSourceManager', false)) {
        let source = 'ROS_POSE';

        if (getFeatureFlagSync('zmqPoseMigration', false)) {
          source = 'SKILLS_BOT';
        } else if (getFeatureFlagSync('complianceMode', false)) {
          source = 'ROS_EFFORT';
        }

        await this.routineContext.armControlBotConnector?.SetControlSource({
          value: source,
        });
      } else {
        await this.routineContext.robot.setROSControlEnabled(true);
      }

      let stopped = false;

      this.events.once('stop', () => {
        stopped = true;
      });

      this.timeout = setTimeout(() => {
        if (stopped) {
          return;
        }

        if (this.args.isBimanual && this.args.isPrimary) {
          try {
            const bots = this.getSecondaryBotEquipments();

            if (bots.length === 0) {
              throw new Error('Secondary robot(s) not connected');
            }

            bots.forEach((bot) =>
              bot.routineEditorVariablesApi.routineEditorVariablesUpdate(
                'inferenceRun',
                {
                  value: false,
                },
              ),
            );
          } catch (error) {
            log.info('Failed to stop secondary robot(s) inference', error);
          }
        }

        this.events.emit('complete');
      }, this.args.timeout * 1000);

      if (!this.args.condition) {
        log.info('No condition provided, using timeout only', 'RunSkillStep');

        return;
      }

      const { condition } = this.args;

      (async () => {
        while (!stopped) {
          try {
            if (await this.routineContext.evaluateConditional(condition)) {
              log.info('Condition met, stopping', 'RunSkillStep');
              this.events.emit('complete');

              if (this.timeout !== null) {
                clearTimeout(this.timeout);
              }

              return;
            }

            await wait(100);
          } catch (error) {
            this.events.emit('failure', {
              failure: {
                kind: FailureKind.InvalidRoutineLoadedFailure,
              },
              failureReason: error.message,
              error,
            });

            break;
          }
        }
      })();
    }
  }
}
